import { memo, useMemo } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import type { PublicView } from '@ringmaster/shared';
import { BroadcastFrame, DESIGN_HEIGHT, SAFE } from './BroadcastFrame.js';
import { ProsceniumFrame } from './ProsceniumFrame.js';
import { SceneStack } from '../fx/SceneStack.js';
import { scenesForGame } from '../assets/scenes/index.js';
import { useSequence } from '../fx/sequencer.js';
import { ParticleLayer } from '../fx/index.js';
import { Glyph, type GlyphName } from '../ui/Glyph.js';
import { ornaments } from './ornaments.js';

/**
 * Stage NightScreen — the night ambient set, covering every night sub-phase
 * (NIGHT_INTRO / NIGHT_WOLVES / NIGHT_INTERLUDE / NIGHT_SEER / RESOLVE_NIGHT).
 *
 * NIGHTFALL sequence on entry: the NIGHT scene (its baked blood moon + glow layer)
 * crossfades in, fog rolls in, and a whose-turn label appears. It NEVER names a
 * player (only wakingRole → a generic ritual line), so a Stage broadcast can't leak
 * identities.
 *
 * The moon is the SceneStack backdrop's own blood moon (night-sky.webp + the
 * night-moon glow layer, which pulses/parallaxes). NightScreen renders no separate
 * moon of its own — the earlier hero moon-disc was a stand-in for the (bugged)
 * invisible scene and now reads as a spurious second moon once the scene renders.
 *
 * Two exports: NightScreenContent (inner) + NightScreenDemo (self-framed). The
 * back-compat NightScreen keeps the old {view, caption} signature.
 */

export interface NightScreenProps {
  view: PublicView;
  isHost: boolean;
}

function wakingLine(role: PublicView['wakingRole']): { glyph: GlyphName; text: string } {
  switch (role) {
    case 'WEREWOLF':
      return { glyph: 'wolfHead', text: 'The pack stirs in the dark' };
    case 'SEER':
      return { glyph: 'seerEye', text: 'The Seer peers into the night' };
    default:
      return { glyph: 'moonFull', text: 'The village sleeps' };
  }
}

function NightScreenContentImpl({ view }: NightScreenProps) {
  const reduced = useReducedMotion();
  const fall = useSequence('NIGHTFALL', { enabled: true });

  const fogIn = fall.has('fogIn');
  const showLabel = fall.has('label');

  const waking = useMemo(() => wakingLine(view.wakingRole), [view.wakingRole]);

  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      {/* The night's blood moon IS the scene backdrop (night-sky.webp's baked moon,
          large and high in the sky). NightScreen renders NO separate hero moon-disc:
          it was a stand-in added while the scene layers were (bugged) invisible, and
          now that the SceneStack renders, a disc on top of the scene moon reads as a
          spurious SECOND moon. The StageHUD ink scrim keeps the timer legible where it
          overlaps the moon. The NIGHTFALL reveal comes from the scene crossfade + fog
          roll-in + ritual label. */}

      {/* Fog bed — rolls in on the fogIn beat. */}
      {fogIn && <ParticleLayer preset="fog" intensity={0.7} slot={2} />}

      {/* Whose-turn ritual label (generic, never a name). */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: DESIGN_HEIGHT * 0.62,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 18,
          zIndex: 5,
        }}
      >
        <AnimatePresence mode="wait">
          {showLabel && (
            <motion.div
              key={view.wakingRole ?? 'sleep'}
              initial={reduced ? { opacity: 0 } : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: reduced ? 0 : 0.5, ease: [0.22, 1, 0.36, 1] }}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 }}
            >
              <Glyph name={waking.glyph} tone={view.wakingRole === 'WEREWOLF' ? 'blood' : 'gold'} size={72} />
              <span
                style={{
                  fontFamily: 'var(--font-display, serif)',
                  fontSize: 'var(--stage-text-title, 96px)',
                  lineHeight: 1.05,
                  color: 'var(--gold-6, #f7e3b0)',
                  textAlign: 'center',
                  letterSpacing: '0.02em',
                }}
              >
                {waking.text}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Round marker, top. */}
      <div
        style={{
          position: 'absolute',
          top: SAFE + 24,
          left: 0,
          right: 0,
          textAlign: 'center',
          fontFamily: 'var(--font-ui, Inter, sans-serif)',
          fontSize: 'var(--stage-text-meta, 24px)',
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: 'var(--blood-6, #ff5a4f)',
          zIndex: 5,
        }}
      >
        Night {view.round}
      </div>
    </div>
  );
}

export const NightScreenContent = memo(NightScreenContentImpl);

/** CardLab / standalone — full frame stack (NIGHT scene). Passes the proscenium
 *  ornaments so this preview matches the live StageView. */
export function NightScreenDemo({ view, isHost }: NightScreenProps) {
  const manifest = scenesForGame('bloodmoon');
  const inner = <NightScreenContent view={view} isHost={isHost} />;
  const framed = (
    <ProsceniumFrame
      cornerSrc={ornaments.filigreeCorner}
      grainSrc={ornaments.grainTile}
    >
      {inner}
    </ProsceniumFrame>
  );
  return (
    <BroadcastFrame>
      {manifest ? (
        <SceneStack manifest={manifest} phase={view.phase}>
          {framed}
        </SceneStack>
      ) : (
        framed
      )}
    </BroadcastFrame>
  );
}
